/**
 * Canonical timeline and padding for aligned chart timestamps.
 * All charts use the same timestamp array; series pad with null where they have no data (no bar drawn).
 */

function toUnixSeconds(ts: string): number {
  const s = ts.slice(0, 10);
  return Math.floor(new Date(s + 'T00:00:00Z').getTime() / 1000);
}

/**
 * Returns sorted Unix timestamps from OHLC series keys (YYYY-MM-DD strings).
 */
export function getTimestampsFromOhlcSeriesKeys(keys: string[]): number[] {
  return keys.map(toUnixSeconds).sort((a, b) => a - b);
}

/**
 * Returns the sorted union of all timestamp arrays (Unix seconds).
 * Used as the single x-axis timeline for all charts.
 */
export function getCanonicalTimestamps(arrays: number[][]): number[] {
  const set = new Set<number>();
  for (const arr of arrays) {
    for (const t of arr) {
      if (Number.isFinite(t)) set.add(t);
    }
  }
  return Array.from(set).sort((a, b) => a - b);
}

/**
 * Builds a series of length canonical.length: for each canonical timestamp,
 * if it exists in sourceTimestamps uses the corresponding sourceValues entry; else nullValue.
 * sourceTimestamps must be sorted (same order as sourceValues).
 */
export function padSeriesToTimeline<T>(
  canonical: number[],
  sourceTimestamps: number[],
  sourceValues: T[],
  nullValue: T
): T[] {
  const map = new Map<number, T>();
  for (let i = 0; i < sourceTimestamps.length; i++) {
    map.set(sourceTimestamps[i], sourceValues[i]);
  }
  return canonical.map((t) => map.get(t) ?? nullValue);
}

/** Date string YYYY-MM-DD from Unix seconds (start of day UTC). */
function toDateString(unixSec: number): string {
  const s = new Date(unixSec * 1000).toISOString();
  return s.slice(0, 10);
}

import type { IndicatorSeriesData } from '../state/types';

/**
 * Returns a value array aligned to canonical timestamps for one indicator.
 * For single-value indicators, raw is Record<dateStr, number>.
 * For multi-field, raw is Record<fieldKey, Record<dateStr, number>>; use field (e.g. 'sma', 'lower') to pick; defaults to first key.
 */
export function getIndicatorValuesOnTimeline(
  raw: IndicatorSeriesData,
  canonicalTimestamps: number[],
  field?: string
): (number | null)[] {
  const isMulti = (x: IndicatorSeriesData): x is Record<string, Record<string, number>> =>
    typeof Object.values(x)[0] === 'object' && Object.values(x)[0] !== null;
  const map = new Map<string, number>();
  if (isMulti(raw)) {
    const key = field && field in raw ? field : Object.keys(raw)[0];
    const series = key ? raw[key] : undefined;
    if (!series) return canonicalTimestamps.map(() => null);
    for (const [dateStr, val] of Object.entries(series)) {
      if (typeof val === 'number' && !Number.isNaN(val)) map.set(dateStr.slice(0, 10), val);
    }
  } else {
    for (const [dateStr, val] of Object.entries(raw)) {
      if (typeof val === 'number' && !Number.isNaN(val)) map.set(dateStr.slice(0, 10), val);
    }
  }
  return canonicalTimestamps.map((t) => map.get(toDateString(t)) ?? null);
}

/** Minimal OHLC shape for timeline helpers (matches store OhlcByInstrument series shape). */
export type OhlcByInstrumentLike = {
  [instrumentId: string]: {
    series: Record<string, Record<string, number>>;
  };
};

function getNum(row: Record<string, number>, ...keys: string[]): number {
  for (const k of keys) {
    const v = row[k] ?? row[k.toLowerCase()];
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
  }
  return 0;
}

/**
 * Returns sorted Unix timestamps for one instrument (only bars with open > 0 and close > 0).
 * Same filter as buildUplotData so canonical aligns with chart data.
 */
export function getInstrumentTimestamps(
  ohlc: OhlcByInstrumentLike | null,
  instrumentId: string
): number[] {
  if (!ohlc) return [];
  const inst = ohlc[instrumentId];
  if (!inst?.series) return [];
  const entries = Object.entries(inst.series)
    .map(([ts, row]) => ({
      t: toUnixSeconds(ts),
      open: getNum(row, 'Open', 'Adj_Open'),
      close: getNum(row, 'Close', 'Adj_Close'),
    }))
    .filter((d) => d.open > 0 && d.close > 0);
  entries.sort((a, b) => a.t - b.t);
  return entries.map((e) => e.t);
}

/**
 * Returns the canonical timestamp array for all time-aligned charts: union of
 * primary instrument, all given instrument ids, and optional equity timestamps.
 * Use this in PrimarySeriesChart, EquityCurveChart, and AdditionalInstrumentChart.
 */
export function getCanonicalTimestampsForAllCharts(
  ohlc: OhlcByInstrumentLike | null,
  primaryInstrumentId: string | null,
  instrumentIds: string[],
  equityTimestamps?: number[]
): number[] {
  const arrays: number[][] = [];
  if (primaryInstrumentId) {
    const primaryTs = getInstrumentTimestamps(ohlc, primaryInstrumentId);
    if (primaryTs.length > 0) arrays.push(primaryTs);
  }
  for (const id of instrumentIds) {
    if (id === primaryInstrumentId) continue;
    const ts = getInstrumentTimestamps(ohlc, id);
    if (ts.length > 0) arrays.push(ts);
  }
  if (equityTimestamps && equityTimestamps.length > 0) arrays.push(equityTimestamps);
  return getCanonicalTimestamps(arrays);
}

export type SplitChartDatum = { chartIndex: number; ratio: number };

/**
 * Returns chart indices and split ratios for the given instrument. Used to draw split
 * annotations when viewing unadjusted data. Only includes bars whose date has a non-zero
 * Split value in the OHLC series.
 */
export function getSplitChartData(
  ohlc: OhlcByInstrumentLike | null,
  instrumentId: string,
  timestamps: number[]
): SplitChartDatum[] {
  if (!ohlc || !timestamps.length) return [];
  const inst = ohlc[instrumentId];
  if (!inst?.series) return [];
  const out: SplitChartDatum[] = [];
  for (const [key, row] of Object.entries(inst.series)) {
    const v = row['Split'] ?? row['split'];
    if (typeof v !== 'number' || Number.isNaN(v) || v === 0) continue;
    const t = toUnixSeconds(key);
    const i = timestamps.indexOf(t);
    if (i >= 0) out.push({ chartIndex: i, ratio: v });
  }
  return out.sort((a, b) => a.chartIndex - b.chartIndex);
}
